

import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  createProducts,
  updateProducts,
} from "../../features/admin/ProductSlice";
import { toast } from "react-toastify";
import { fetchCompanies } from "../../features/admin/CompanySlice";
import { fetchCategoryByTypePublic } from "../../features/admin/CategorySlice";
import { convertToSlug, formatCurrencyVND } from "../../utils/helps";
import { productStatus } from "../../utils/defaultValue";
import ImageManagerModal from "./ImageManagerModal";
import ApiCustom from "../../utils/ApiCustom";
import { PhotoIcon } from "@heroicons/react/24/outline";
import TextEditor from "../TextEditor";

const productSchema = Yup.object().shape({
  name: Yup.string().trim().required("Tên sản phẩm không được để trống"),
  slug: Yup.string().trim().required("Slug không được để trống"),
  price: Yup.string()
    .required("Giá tiền không được để trống")
    .test("isNotZero", "Giá tiền phải lớn hơn 0", (value) => {
      return value !== "0";
    }),

  quantity: Yup.string().required("Số lượng không được để trống"),
  priority: Yup.string().required("Số lượng không được để trống"),
  categoryId: Yup.number()
    .typeError("Danh mục không hợp lệ")
    .positive("Danh mục không hợp lệ")
    .required("Danh mục không được để trống"),
  madeBy: Yup.string().trim(),
  status: Yup.string().trim(),
  description: Yup.string().trim(),
  unit: Yup.string().trim().required("Đơn vị không được để trống"),
});

const fieldLabels = {
  name: "Tên sản phẩm",
  description: "Mô tả",
  price: "Giá tiền",
  discountPrice: "Giá tiền khuyến mãi",
  quantity: "Số lượng",
  status: "Trạng thái",
  companyId: "Công ty",
  companyURL: "Link công ty",
  slug: "Slug sản phẩm",
  urlImage: "Hình ảnh sản phẩm",
  madeBy: "Trang web được thiết kế bởi",
  priority: "Ưu tiên",
  categoryId: "Danh mục",
  unit: "Đơn vị tính",
  gift: "Quà tặng",
  giftCondition: "Số tiền nhận quà",
};

const ProductModal = ({ productSlug, company, onClose }) => {
  const [product, setProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    discountPrice: "",
    quantity: 1,
    status: "available",
    companyId: 0,
    companyURL: "",
    slug: "",
    urlImage: "",
    madeBy: "KATEC",
    priority: 0,
    categoryId: 0,
    gift: "",
    giftCondition: "",
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState("");
  const [hasNewImage, setHasNewImage] = useState(false);
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.products);
  const { categories } = useSelector((state) => state.categories);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePressImage = (media) => {
    setIsModalOpen(false);
    setHasNewImage(true);
    const imagePreviewChange = media.url;
    setImagePreview(imagePreviewChange);
    setFormData({ ...formData, urlImage: media.url });
  };

  const getMembershipFee = () => {
    ApiCustom.get(`/product-show/${productSlug}`)
      .then((res) => {
        setProduct(res.data?.data);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    if (product) {
      setFormData(product);
      setHasNewImage(true);
      const imagePreviewChange = product.urlImage;
      setImagePreview(imagePreviewChange);
    }
  }, [product]);

  useEffect(() => {
    if (productSlug) {
      getMembershipFee();
    }
    dispatch(fetchCompanies());
    dispatch(fetchCategoryByTypePublic());
  }, []);

  const getImageUrl = () => {
    if (hasNewImage) {
      return imagePreview;
    } else if (product && product.images) {
      return product.images;
    }
    return "";
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    const rawValue = value.replace(/[^0-9]/g, "");

    setFormData((prev) => ({
      ...prev,
      [name]: rawValue === "" ? "" : Number(rawValue),
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));

    try {
      await Yup.reach(productSchema, name).validate(rawValue);
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (validationError) {
      setErrors((prev) => ({ ...prev, [name]: validationError.message }));
    }

    setTimeout(() => {
      const inputElement = e.target;
      const formattedValue = formatCurrencyVND(Number(rawValue));

      const position = formattedValue.length - 2;
      inputElement.setSelectionRange(position, position);
    }, 0);
  };

  const userId = localStorage.getItem("userId");
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const normalizedFormData = {
        ...formData,
        discountPrice:
          formData.discountPrice === "" ? 0 : formData.discountPrice,
        giftCondition:
          formData.giftCondition === "" ? null : formData.giftCondition,
      };

      const updatedFormData =
        userId == 1
          ? normalizedFormData
          : {
              ...normalizedFormData,
              companyId: company.id,
              companyURL: company.websiteOrHotline,
            };

      if (updatedFormData.urlImage == "") {
        toast.error("Vui lòng chọn hình ảnh");
        return;
      }

      await productSchema.validate(updatedFormData, { abortEarly: false });
      if (product) {
        await dispatch(
          updateProducts({ id: product.id, newPost: updatedFormData }),
        ).unwrap();
        toast.success("Cập nhật thành công!");
        onClose();
      } else {
        await dispatch(createProducts(updatedFormData)).unwrap();
        toast.success("Thêm sản phẩm mới thành công!");
        onClose();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
        console.error("Lỗi khi lưu sản phẩm:", error);
      }
    }
  };

  const handleChangeDescription = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold ">
              {product ? "Chỉnh sửa thông tin sản phẩm" : "Thêm sản phẩm mới"}
            </h2>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            >
              X
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {fieldLabels.name}
                      <i className="text-red-600"> *</i>
                    </label>
                    <input
                      type="text"
                      name={formData.name}
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          name: e.target.value,
                          slug: convertToSlug(e.target.value),
                        });
                        setErrors({ ...errors, name: "", slug: "" });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.name ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {fieldLabels.slug}
                      <i className="text-red-600"> *</i>
                    </label>
                    <input
                      type="text"
                      name={formData.slug}
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      className={`w-full p-2 border rounded-md ${
                        errors.slug ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    {errors.slug && (
                      <p className="mt-1 text-sm text-red-600">{errors.slug}</p>
                    )}
                  </div>
                  {["price", "discountPrice", "priority"].map((key) => (
                    <div key={key}>
                      <label className="block text-sm font-medium  mb-1">
                        {fieldLabels[key]}
                        {key != "discountPrice" && (
                          <i className="text-red-600"> *</i>
                        )}
                      </label>
                      <input
                        disabled={key === "priority" && userId != 1}
                        type="text"
                        name={key}
                        value={
                          key === "price" || key === "discountPrice"
                            ? formatCurrencyVND(formData[key])
                            : formData[key]
                        }
                        onChange={handleChange}
                        className={`w-full p-2 border rounded-md ${
                          key != "discountPrice" && errors[key]
                            ? "border-red-500"
                            : "border-gray-300"
                        } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      />
                      {key != "discountPrice" && errors[key] && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors[key]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.categoryId}
                      <i className="text-red-600"> *</i>
                    </label>
                    <select
                      name={formData.categoryId}
                      value={formData.categoryId}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          categoryId: e.target.value,
                        });
                        setErrors({
                          ...errors,
                          categoryId: "",
                        });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.categoryId ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="">Chọn một tùy chọn</option>
                      {categories.map((item, idx) => (
                        <option key={idx} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {errors.categoryId && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.categoryId}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.madeBy}
                    </label>
                    <select
                      name={formData.madeBy}
                      value={formData.madeBy}
                      onChange={(e) => {
                        setFormData({ ...formData, madeBy: e.target.value });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.madeBy ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="KATEC">Công ty hội viên</option>
                      <option value="OTHER">Công ty khác</option>
                    </select>
                    {errors.madeBy && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.madeBy}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.status}
                    </label>
                    <select
                      name={formData.status}
                      value={formData.status}
                      onChange={(e) => {
                        setFormData({ ...formData, status: e.target.value });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.status ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      {productStatus.map((item, idx) => (
                        <option key={idx} value={item.key}>
                          {item.value}
                        </option>
                      ))}
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.status}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.unit}
                      <i className="text-red-600"> *</i>
                    </label>
                    <input
                      type="text"
                      name={formData.unit}
                      value={formData.unit}
                      onChange={(e) => {
                        setFormData({ ...formData, unit: e.target.value });
                        setErrors({ ...error, unit: "" });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.unit ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                    {errors.unit && (
                      <p className="mt-1 text-sm text-red-600">{errors.unit}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.gift}
                    </label>
                    <input
                      type="text"
                      name={formData.gift}
                      value={formData.gift}
                      onChange={(e) => {
                        setFormData({ ...formData, gift: e.target.value });
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.gift ? "border-red-500" : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium  mb-1">
                      {fieldLabels.giftCondition}
                    </label>
                    <input
                      type="text"
                      name={formData.giftCondition}
                      value={formatCurrencyVND(formData.giftCondition)}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, "");

                        setFormData((prev) => ({
                          ...prev,
                          giftCondition:
                            rawValue === "" ? "" : Number(rawValue),
                        }));

                        setTimeout(() => {
                          const inputElement = e.target;
                          const formattedValue = formatCurrencyVND(
                            Number(rawValue),
                          );

                          const position = formattedValue.length - 2;
                          inputElement.setSelectionRange(position, position);
                        }, 0);
                      }}
                      className={`w-full p-2 border rounded-md ${
                        errors.giftCondition
                          ? "border-red-500"
                          : "border-gray-300"
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom section - Image Upload and Note */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium  mb-1">
                    Ảnh sản phẩm <span className="text-red-500"> *</span>
                  </label>
                  <div className="mt-1 flex items-center space-x-4">
                    <div className="w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                      {getImageUrl() ? (
                        <img
                          src={getImageUrl()}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <PhotoIcon className="w-10 h-10 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col space-y-2">
                      <div
                        className="cursor-pointer py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        onClick={() => handleOpenModal()}
                      >
                        {product ? "Cập nhật ảnh" : "Chọn ảnh"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  {fieldLabels.description}
                </label>

                <TextEditor
                  value={formData.description}
                  name="description"
                  onChange={handleChangeDescription}
                  importCustomInsertImage={true}
                />
              </div>
            </div>

            {/* Footer - Action Buttons */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                {product ? "Cập nhật" : "Thêm mới"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {isModalOpen && (
        <ImageManagerModal
          onClose={handleCloseModal}
          onPress={handlePressImage}
          multiImage={true}
          selectedMultiMedia={[]}
          deActiveInsertImage={true}
        />
      )}
    </div>
  );
};

export default ProductModal;
